import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Card {
  rank: string;
  suit: string;
  rotate?: number;
}

interface GamePlayer {
  [player: string]: { cardCount: number };
}

interface GameState {
  middle: Card[];
  hand: Card[];
  gameId: string | null;
  showMiddle: boolean;
  playerTurn: string;
  somethingHappend: string | null;
  players: GamePlayer;
  openGameOverModal: boolean;
  currentRound: number;
  isOffline: boolean;
}

const initialState: GameState = {
  middle: [],
  hand: [],
  gameId: null,
  showMiddle: true,
  playerTurn: "",
  somethingHappend: "",
  players: {},
  openGameOverModal: false,
  currentRound: 1,
  isOffline: false
};

export const gameSlice = createSlice({
  name: "game",
  initialState,
  reducers: {
    setMiddle: (state, action: PayloadAction<{ middle: Card[] }>) => {
      state.middle = action.payload.middle;
    },

    appendMiddle: (state, action: PayloadAction<{ card: Card }>) => {
      state.middle.push(action.payload.card);
    },

    setHand: (state, action: PayloadAction<{ hand: Card[] }>) => {
      state.hand = action.payload.hand;
    },

    removeFromHand: (state, action: PayloadAction<Pick<Card, 'rank' | 'suit'>>) => {
      state.hand = state.hand.filter(
        card => !(card.rank === action.payload.rank && card.suit === action.payload.suit)
      );
    },

    setGameId: (state, action: PayloadAction<{ gameId: string }>) => {
      state.gameId = action.payload.gameId;
    },

    setPlayerTurn: (state, action: PayloadAction<{ player: string }>) => {
      state.playerTurn = action.payload.player;
    },

    setSomethingHappend: (state, action: PayloadAction<{ message: string | null }>) => {
      state.somethingHappend = action.payload.message;
    },

    setPlayersInitially: (state, action: PayloadAction<{ players: string[] }>) => {
      const gamePlayer: GamePlayer = {};
      action.payload.players.forEach(p => {
        gamePlayer[p] = { cardCount: 4 };
      });
      state.players = gamePlayer;
    },

    reducePlayerCardCount: (state, action: PayloadAction<{ player: string }>) => {
      state.players[action.payload.player].cardCount--;
    },

    resetPlayerCardCount: (state) => {
      Object.keys(state.players).forEach(p => {
        state.players[p].cardCount = 4;
      });
    },

    setOpenGameOverModal: (state, action: PayloadAction<{ value: boolean }>) => {
      state.openGameOverModal = action.payload.value;
    },

    resetGameState: () => initialState,

    incrementRound: (state) => {
      state.currentRound += 1;
    },

    setOfflineMode: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },

    setGameState: (
      state,
      action: PayloadAction<{
        hand: Card[];
        middle: Card[];
        playerTurn: string;
        players: { [player: string]: number };
        currentRound: number;
      }>
    ) => {
      state.hand = action.payload.hand;
      state.middle = action.payload.middle;
      state.playerTurn = action.payload.playerTurn;
      state.players = Object.fromEntries(
        Object.entries(action.payload.players).map(([name, cardCount]) => [name, { cardCount }])
      );
      state.currentRound = action.payload.currentRound;
    }
  }
});

export const {
  setMiddle,
  appendMiddle,
  setHand,
  removeFromHand,
  setGameId,
  setPlayerTurn,
  setSomethingHappend,
  setPlayersInitially,
  reducePlayerCardCount,
  resetPlayerCardCount,
  setOpenGameOverModal,
  resetGameState,
  incrementRound,
  setOfflineMode,
  setGameState
} = gameSlice.actions;

export default gameSlice.reducer;
